import { BodyType, customInstance } from '@/features/auth/axios-config'
import {
  RequestAssignPermission,
  RequestCreateInvitation,
  RequestFilters,
  RequestUpdateUser,
  SuccessResponseResponseInvitation,
  SuccessResponseResponseUserWithPermission,
} from '@/schema'
import { mergeTablePaginationParams } from '@/utils'

import { API_ENDPOINT_USER_MANAGEMENT } from './constants'
import { DeleteUserResponse, UserListResponse } from './types'

export type SecondParameter<T extends (...args: never) => unknown> =
  Parameters<T>[1]

export const userManagementApi = {
  listUsers: async (
    data: Partial<RequestFilters> = {}
  ): Promise<UserListResponse> => {
    const requestBody = mergeTablePaginationParams(data)

    return customInstance<UserListResponse>({
      method: 'post',
      url: `${API_ENDPOINT_USER_MANAGEMENT}/list`,
      data: requestBody,
    })
  },

  deleteUser: async (id: string): Promise<DeleteUserResponse> => {
    return customInstance<DeleteUserResponse>({
      method: 'delete',
      url: `${API_ENDPOINT_USER_MANAGEMENT}/${id}`,
    })
  },
}

/**
 * Get user profile with enriched role assignments and scope details (projects, deliverable sets)
 * @summary Get user by ID with role assignments
 */
export const getUserById = (
  userId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseResponseUserWithPermission>(
    { url: `/admin/v1/users/${userId}`, method: 'GET', signal },
    options
  )
}

/**
 * Create a user invitation that delegates to Ascend ID for Entra provisioning and creates a shadow user in Vista
 * @summary Create a user invitation via Ascend ID
 */
export const createInvitation = (
  data: BodyType<RequestCreateInvitation>,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseResponseInvitation>(
    {
      url: `/admin/v1/users/invitations`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data,
      signal,
    },
    options
  )
}

/**
 * Update user profile information and trigger denormalization sync
 * @summary Update user profile
 */
export const updateUserById = (
  userId: string,
  data: BodyType<RequestUpdateUser>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SuccessResponseResponseUserWithPermission>(
    {
      url: `/admin/v1/users/${userId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data,
    },
    options
  )
}

/**
 * Assign roles with scoped permissions. Uses full-replacement semantics: existing scopes for the specified role are completely replaced. Empty scopes array removes all project assignments but preserves the base role.
 * @summary Assign or edit project-scoped permissions for a user
 */
export const updateUserPermissions = (
  userId: string,
  data: BodyType<RequestAssignPermission>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SuccessResponseResponseUserWithPermission>(
    {
      url: `/admin/v1/users/${userId}/permissions`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data,
    },
    options
  )
}
